import { getFilterType, normalizeFilterConfigs } from '../FilterBar/filterConfig';
import {
  getSelectableLabelsForGrain,
  inferCategorySelectionGrain,
} from '../../../constants/filterCategories';
import { getCompanyDisplayName } from '../../../utils/companyHelpers';

const MAX_VISIBLE_VALUES = 3;
const MULTI_SELECT_FILTER_TYPES = new Set([
  'assetClassFilter',
  'category',
  'categoryFlat',
  'groupedMultiSelect',
  'indexCategory',
  'status',
  'tableFields',
  'ticker',
]);

const normalizeText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const uniqueValues = (values) => [...new Set((Array.isArray(values) ? values : [values])
  .map((value) => normalizeText(value))
  .filter(Boolean))];

const getOptionValue = (option) => (
  option && typeof option === 'object'
    ? option.value ?? option.ticker ?? option.id ?? option.field ?? option.label
    : option
);

const getOptionLabel = (option) => (
  option && typeof option === 'object'
    ? option.label ?? option.sublabel ?? option.name ?? option.field ?? option.value ?? option.id
    : option
);

const resolveOptions = (filter) => {
  const candidates = filter.props?.options
    || filter.props?.allTickers
    || filter.props?.allColDefs
    || [];
  return Array.isArray(candidates) ? candidates : [];
};

const mapValueToLabel = (value, options, filterType) => {
  const match = options.find((option) => String(getOptionValue(option)) === String(value));
  if (filterType === 'ticker') {
    const ticker = match?.ticker || value?.ticker || value;
    const companyName = match?.name || match?.companyName || value?.name || value?.companyName;
    return normalizeText(getCompanyDisplayName(ticker, companyName));
  }
  return normalizeText(match ? getOptionLabel(match) : value);
};

const getRawValues = (filter, value) => {
  const resolver = filter.exportValue || filter.props?.exportValue;
  if (typeof resolver === 'function') return resolver(value, filter);
  if (resolver !== undefined) return resolver;
  return value;
};

const getAllSelectedLabel = (filter) => {
  if (filter.exportAllLabel || filter.props?.exportAllLabel) {
    return filter.exportAllLabel || filter.props.exportAllLabel;
  }
  if (filter.type === 'category' || filter.type === 'categoryFlat' || filter.type === 'indexCategory') {
    return 'All categories';
  }
  return `All ${String(filter.sublabel || filter.id || 'values').toLowerCase()}`;
};

const getSelectableCount = (filter, rawValues, options) => {
  const configuredCount = Number(filter.exportTotalCount || filter.props?.exportTotalCount);
  if (configuredCount > 0) return configuredCount;
  if (filter.type === 'category') {
    const grain = filter.props?.selectionGrain
      || inferCategorySelectionGrain(rawValues, filter.props?.selectionDepth);
    return getSelectableLabelsForGrain(grain, {
      availableMarkets: filter.props?.availableMarkets,
    }).length;
  }
  return options.length;
};

export const compactChartExportValues = ({
  values,
  allSelected = false,
  allSelectedLabel = 'All values',
  maxVisible = MAX_VISIBLE_VALUES,
}) => {
  const normalized = uniqueValues(values);
  if (allSelected && normalized.length > 0) return [allSelectedLabel];
  if (normalized.length <= maxVisible) return normalized;
  return [...normalized.slice(0, maxVisible), `+${normalized.length - maxVisible} more`];
};

export const buildChartExportFilters = ({ filters = [], state = {} } = {}) => (
  normalizeFilterConfigs(filters).flatMap((filter) => {
    if (filter.export === false || filter.props?.export === false) return [];
    const filterType = getFilterType(filter.type);
    const stateKey = filter.stateKey || filterType.stateKey || filter.id;
    const rawValue = getRawValues(filter, state[stateKey]);
    if (rawValue === undefined || rawValue === null || rawValue === '') return [];

    const options = resolveOptions(filter);
    const rawValues = Array.isArray(rawValue) ? rawValue : [rawValue];
    const values = uniqueValues(rawValues.map((value) => mapValueToLabel(value, options, filter.type)));
    if (!values.length) return [];

    const selectableCount = getSelectableCount(filter, rawValues, options);
    const allSelected = filter.exportAllSelected === true
      || filter.props?.exportAllSelected === true
      || (MULTI_SELECT_FILTER_TYPES.has(filter.type)
        && selectableCount > 0
        && rawValues.length >= selectableCount);

    return [{
      id: filter.id,
      label: normalizeText(filter.exportLabel || filter.sublabel || filter.props?.label || filter.id),
      values: compactChartExportValues({
        values,
        allSelected,
        allSelectedLabel: getAllSelectedLabel(filter),
      }),
    }];
  })
);

export const formatChartExportFilters = (filters = []) => filters.flatMap((filter) => {
  const label = normalizeText(filter?.label);
  const values = uniqueValues(filter?.values || filter?.value);
  if (!values.length) return [];
  return [label ? `${label}: ${values.join(', ')}` : values.join(', ')];
});
